/**
 * useTechnicianDiagnosticStats Hook
 * 
 * Fetches diagnostic competency stats for a technician.
 * Used by Technician Passport (manager-only view).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  TechnicianDiagnosticStats,
  DiagnosticSampleStatus,
} from '@/types/diagnosticIntelligence';
import { getSampleStatus } from '@/utils/diagnosticScoringEngine';

export interface DiagnosticStatsResult {
  overall: TechnicianDiagnosticStats | null;
  byCategory: TechnicianDiagnosticStats[];
  totalDiagnostics: number;
  overallDCS: number | null;
  overallSampleStatus: DiagnosticSampleStatus;
  topCategories: Array<{
    category: string;
    dcs: number;
    count: number;
  }>;
  improvementAreas: Array<{
    category: string;
    dcs: number;
    recommendation: string;
  }>;
}

/**
 * Fetch diagnostic stats for a technician
 */
export function useTechnicianDiagnosticStats(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['technician-diagnostic-stats', technicianProfileId],
    queryFn: async (): Promise<DiagnosticStatsResult> => {
      if (!technicianProfileId) {
        return {
          overall: null,
          byCategory: [],
          totalDiagnostics: 0,
          overallDCS: null,
          overallSampleStatus: 'learning',
          topCategories: [],
          improvementAreas: [],
        };
      }

      // Fetch all stats for this technician
      const { data, error } = await supabase
        .from('technician_diagnostic_stats')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('total_diagnostics', { ascending: false });

      if (error) throw error;

      // Type assertion since table is new
      const stats = (data || []) as TechnicianDiagnosticStats[];

      // Separate overall from category-specific
      const overall = stats.find(s => s.category_key === null) || null;
      const byCategory = stats.filter(s => s.category_key !== null);

      // Calculate totals
      const totalDiagnostics = overall?.total_diagnostics || 
        byCategory.reduce((sum, s) => sum + (s.total_diagnostics || 0), 0);
      
      const overallDCS = overall?.diagnostic_competency_score || null;
      // Scoring version 2 (Oct 20 S2) states the sample status from DEDICATED
      // diagnostic evidence; counting inspections here would call 100
      // inspections "expert". Version-1 rows keep the old reading.
      const overallSampleStatus = (overall?.scoring_version ?? 0) >= 2
        ? overall!.sample_status
        : getSampleStatus(totalDiagnostics);
      // A category with no dedicated diagnostic evidence (e.g. an inspection
      // category) is neither a diagnostic strength nor a growth area.
      const hasDiagnosticEvidence = (s: TechnicianDiagnosticStats) =>
        (s.scoring_version ?? 0) < 2 || (s.dedicated_diagnostic_scored ?? 0) > 0;

      // Find top performing categories
      const topCategories = byCategory
        .filter(s => hasDiagnosticEvidence(s) && s.diagnostic_competency_score !== null && s.total_diagnostics >= 3)
        .sort((a, b) => (b.diagnostic_competency_score || 0) - (a.diagnostic_competency_score || 0) || (a.category_key || '').localeCompare(b.category_key || ''))
        .slice(0, 3)
        .map(s => ({
          category: s.category_key!,
          dcs: s.diagnostic_competency_score!,
          count: s.total_diagnostics,
        }));

      // Find improvement areas (low DCS with enough samples)
      const improvementAreas = byCategory
        .filter(s => 
          hasDiagnosticEvidence(s) &&
          s.diagnostic_competency_score !== null && 
          s.diagnostic_competency_score < 60 &&
          s.total_diagnostics >= 5
        )
        .sort((a, b) => (a.diagnostic_competency_score || 0) - (b.diagnostic_competency_score || 0))
        .slice(0, 3)
        .map(s => ({
          category: s.category_key!,
          dcs: s.diagnostic_competency_score!,
          recommendation: getImprovementRecommendation(s),
        }));

      return {
        overall,
        byCategory,
        totalDiagnostics,
        overallDCS,
        overallSampleStatus,
        topCategories,
        improvementAreas,
      };
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * Generate improvement recommendation based on stats
 */
function getImprovementRecommendation(stats: TechnicianDiagnosticStats): string {
  if (stats.calibration_score !== null && stats.calibration_score < 50) {
    return 'Focus on confidence calibration - align confidence with outcomes';
  }
  if (stats.outcome_success_rate !== null && stats.outcome_success_rate < 60) {
    return 'Review diagnostic methodology for improved accuracy';
  }
  if (stats.complaint_validation_rate !== null && stats.complaint_validation_rate < 50) {
    return 'Improve complaint verification and confirmation process';
  }
  if (stats.avg_diagnostic_minutes !== null && stats.avg_diagnostic_minutes > 60) {
    return 'Consider efficiency improvements in diagnostic workflow';
  }
  return 'Continue building experience in this category';
}

/**
 * Fetch recent mismatch events for a technician
 */
export function useTechnicianMismatchEvents(
  technicianProfileId: string | undefined,
  limit: number = 20
) {
  return useQuery({
    queryKey: ['technician-mismatch-events', technicianProfileId, limit],
    queryFn: async () => {
      if (!technicianProfileId) return [];

      const { data, error } = await supabase
        .from('diagnostic_mismatch_events')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    },
    enabled: !!technicianProfileId,
  });
}
